import { useMemo } from "react";
import { Alert, Card, DataTable, type ColumnDef } from "@/deps/ui-library";
import { Link } from "@tanstack/react-router";
import { useWithholdingReport } from "@/api/queries";
import type { WithholdingReport } from "@/api/types";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { Figure, MonthField, useMonthParam } from "./reportParts";
import { ReportFrame } from "./ReportFrame";
import { useTaxWords } from "@/app/session";

type Withheld = WithholdingReport["withheld"][number];
type ByCustomer = WithholdingReport["byCustomers"][number];

const documentLink = (number: string) => (
  <Link to="/doc/$number" params={{ number }} className="num text-primary hover:underline">
    {number}
  </Link>
);

const withheldColumns = (say: (text: string) => string): Array<ColumnDef<Withheld>> => [
  {
    id: "document",
    header: "Document",
    cell: ({ row: { original: r } }) => documentLink(r.number),
  },
  {
    id: "supplier",
    header: say("Supplier · PAN"),
    cell: ({ row: { original: r } }) => (
      <span>
        {r.supplierName}
        {r.supplierTaxId ? <span className="text-fg-muted"> · {r.supplierTaxId}</span> : null}
      </span>
    ),
  },
  { id: "category", header: "Category", cell: ({ row: { original: r } }) => r.code },
  {
    id: "rate",
    header: "Rate",
    meta: { textAlign: "right", numeric: true },
    cell: ({ row: { original: r } }) => `${r.ratePct}%`,
  },
  {
    id: "base",
    header: "Base",
    meta: { textAlign: "right", numeric: true },
    cell: ({ row: { original: r } }) => formatAmount(r.base),
  },
  {
    id: "withheld",
    header: "Withheld",
    meta: { textAlign: "right", numeric: true },
    cell: ({ row: { original: r } }) => formatAmount(r.amount),
  },
];

const BY_CUSTOMERS: Array<ColumnDef<ByCustomer>> = [
  {
    id: "document",
    header: "Document",
    cell: ({ row: { original: r } }) => documentLink(r.number),
  },
  { id: "customer", header: "Customer", cell: ({ row: { original: r } }) => r.customerName },
  { id: "category", header: "Category", cell: ({ row: { original: r } }) => r.code ?? "" },
  {
    id: "withheld",
    header: "Withheld",
    meta: { textAlign: "right", numeric: true },
    cell: ({ row: { original: r } }) => formatAmount(r.amount),
  },
];

/** What was withheld from suppliers and owed to the tax office, and what customers kept back from you. */
export function TdsWithheldPage() {
  const say = useTaxWords();
  const { month, months, setMonth, from, to } = useMonthParam("/reports/tds-withheld");
  const { data, isPending, error } = useWithholdingReport(from, to);
  const period = `${formatDate(from)} – ${formatDate(to)}`;
  const fromYou = useMemo(
    () => (data?.byCustomers ?? []).reduce((sum, r) => sum + Number(r.amount), 0).toFixed(2),
    [data],
  );

  return (
    <ReportFrame
      id="tds"
      title={say("TDS withheld")}
      period={period}
      exportable={
        data
          ? () => ({
              title: say("TDS withheld"),
              period,
              columns: [
                { header: "Document" },
                { header: "Party" },
                { header: say("PAN") },
                { header: "Category" },
                { header: "Rate %", numeric: true },
                { header: "Base", numeric: true },
                { header: "Withheld", numeric: true },
              ],
              rows: [
                ...data.withheld.map((r) => [
                  r.number,
                  r.supplierName,
                  r.supplierTaxId,
                  r.code,
                  r.ratePct,
                  r.base,
                  r.amount,
                ]),
                ...data.byCustomers.map((r) => [
                  r.number,
                  r.customerName,
                  null,
                  r.code,
                  null,
                  null,
                  r.amount,
                ]),
              ],
            })
          : undefined
      }
    >
      <div className="flex flex-wrap items-end gap-2">
        <MonthField month={month} months={months} onChange={setMonth} />
      </div>

      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={error instanceof Error ? error.message : "That report could not be loaded."}
        />
      ) : null}

      {data ? (
        <div className="flex flex-wrap gap-3">
          <Figure
            label="Withheld from suppliers"
            value={data.totalWithheld}
            detail={`on a base of ${formatAmount(data.totalBase)}`}
          />
          <Figure label="Withheld from you" value={fromYou} />
        </div>
      ) : null}

      <h2 className="text-fg text-sm font-medium">Withheld on payments to suppliers</h2>
      <Card size="none" className="overflow-hidden">
        <DataTable
          aria-label="Withheld on payments to suppliers"
          size="sm"
          enableSorting={false}
          columns={withheldColumns(say)}
          data={data?.withheld ?? []}
          getRowId={(r) => r.documentId}
          loading={isPending}
          emptyContent="Nothing was withheld from a supplier this month."
          pagination={{ mode: "manual", show: false }}
          containerClassName="rounded-none border-0"
        />
      </Card>

      <h2 className="text-fg text-sm font-medium">Withheld by customers on your receipts</h2>
      <Card size="none" className="overflow-hidden">
        <DataTable
          aria-label="Withheld by customers on your receipts"
          size="sm"
          enableSorting={false}
          columns={BY_CUSTOMERS}
          data={data?.byCustomers ?? []}
          getRowId={(r) => r.receiptId}
          loading={isPending}
          emptyContent="No customer withheld anything this month."
          pagination={{ mode: "manual", show: false }}
          containerClassName="rounded-none border-0"
        />
      </Card>
    </ReportFrame>
  );
}
